import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ApiError } from '../../api/core/ApiError';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // For 401 errors, don't show the error boundary if we're already redirecting
    // The catchErrorCodes function will handle logout and redirect
    if (error instanceof ApiError && error.status === 401) {
      // Check if we're already on the login page or redirecting
      if (window.location.pathname === '/login') {
        // Already on login page, don't show error
        return;
      }
    }

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const isApiError = error instanceof ApiError;
      const isUnauthorized = isApiError && error.status === 401;

      // For unauthorized errors, show a simpler message
      if (isUnauthorized) {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              p: 3,
              backgroundColor: '#f5f5f5',
            }}
          >
            <Paper
              sx={{
                p: 4,
                maxWidth: 500,
                textAlign: 'center',
                boxShadow: 3,
              }}
            >
              <Typography variant="h5" color="error" gutterBottom>
                Sesión Expirada
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Su sesión ha expirado. Inicie sesión de nuevo.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  window.location.href = '/login';
                }}
              >
                Ir al Login
              </Button>
            </Paper>
          </Box>
        );
      }

      // For other errors, show full error details
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            p: 3,
            backgroundColor: '#f5f5f5',
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 800,
              boxShadow: 3,
            }}
          >
            <Typography variant="h5" color="error" gutterBottom>
              Oops! Algo salió mal
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              {error?.message || 'Ha ocurrido un error inesperado'}
            </Typography>

            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#fafafa',
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Detalles del error (solo en desarrollo):
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={this.handleReset}>
                Intentar de nuevo
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  window.location.href = '/dashboard';
                }}
              >
                Volver al Dashboard
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
